import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@ouropix/ui';
import { Card, CardContent } from '@ouropix/ui';
import { Badge } from '@ouropix/ui';
import { Skeleton } from '@ouropix/ui';
import { ConfirmModal } from '@ouropix/ui';
import { Plus, Pencil, Trash2, Gift, Package, Radio, Banknote, Ban } from 'lucide-react';
import { toast } from 'sonner';
import { apiErrorMessage } from '@/lib/api-helpers';
import { api } from '@/lib/api';
import { PrizeFormDialog } from '@/components/prizes/PrizeFormDialog';

export const Route = createFileRoute('/_admin/premios')({
  component: PrêmiosPage,
});

interface Prize {
  id: string;
  name: string;
  value: number;
  image_url?: string | null;
  imageUrl?: string | null;
  prize_type?: 'cash' | 'physical' | 'zero';
  prizeType?: 'cash' | 'physical' | 'zero';
  active: boolean;
  show_in_live_winners?: boolean;
  showInLiveWinners?: boolean;
  created_at?: string;
  createdAt?: string;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
}

function getPrizeImage(p: Prize) { return p.image_url ?? p.imageUrl ?? null; }
function getPrizeType(p: Prize) { return p.prize_type ?? p.prizeType ?? 'cash'; }
function getShowLive(p: Prize) { return p.show_in_live_winners ?? p.showInLiveWinners ?? false; }

function PrêmiosPage() {
  const queryClient = useQueryClient();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedPrize, setSelectedPrize] = useState<Prize | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const { data: prizes, isPending, isError, error: queryError } = useQuery({
    queryKey: ['admin-prizes'],
    queryFn: () =>
      api
        .get<{ prizes: Prize[]; pagination?: unknown }>('/admin/prizes')
        .then((r) => r.data.prizes ?? []),
    retry: 2,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/admin/prizes/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-prizes'] });
      toast.success('Prêmio excluído com sucesso!');
      setDeleteId(null);
    },
    onError: (err) => {
      toast.error(apiErrorMessage(err, 'Erro ao excluir prêmio'));
    },
  });

  const handleEdit = (prize: Prize) => {
    setSelectedPrize(prize);
    setIsFormOpen(true);
  };

  const handleCreate = () => {
    setSelectedPrize(null);
    setIsFormOpen(true);
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setSelectedPrize(null);
  };

  if (isPending) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <Skeleton className="h-8 w-48 mb-2" />
            <Skeleton className="h-4 w-72" />
          </div>
          <Skeleton className="h-10 w-32" />
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-72" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Gift className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-3xl font-bold">Biblioteca de Prêmios</h1>
            <p className="text-muted-foreground">Gerencie os prêmios disponíveis para as raspadinhas</p>
          </div>
        </div>
        <Button onClick={handleCreate}>
          <Plus className="h-4 w-4 mr-2" />
          Novo Prêmio
        </Button>
      </div>

      {isError && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-6 text-center">
          <Gift className="h-10 w-10 text-destructive mx-auto mb-2" />
          <p role="alert" className="text-destructive font-medium">Erro ao carregar prêmios</p>
          <p className="text-sm text-muted-foreground mt-1">
            {(queryError as Error)?.message || 'Tente recarregar a página'}
          </p>
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {prizes?.map((prize) => (
          <Card key={prize.id} className="overflow-hidden">
            <div className="aspect-square relative bg-muted">
              {getPrizeImage(prize) ? (
                <img
                  loading="lazy"
                  decoding="async"
                  src={getPrizeImage(prize)!}
                  alt={prize.name}
                  className="w-full h-full object-contain p-4"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Gift className="h-16 w-16 text-muted-foreground" />
                </div>
              )}
              <div className="absolute top-2 right-2 flex gap-1 flex-wrap justify-end">
                {getPrizeType(prize) === 'physical' ? (
                  <Badge variant="secondary" className="gap-1 bg-blue-600 hover:bg-blue-600 text-white">
                    <Package className="h-3 w-3" />
                    Item
                  </Badge>
                ) : getPrizeType(prize) === 'zero' ? (
                  <Badge variant="secondary" className="gap-1 bg-zinc-500 hover:bg-zinc-500 text-white">
                    <Ban className="h-3 w-3" />
                    Sem Prêmio
                  </Badge>
                ) : (
                  <Badge variant="secondary" className="gap-1 bg-emerald-600 hover:bg-emerald-600 text-white">
                    <Banknote className="h-3 w-3" />
                    Dinheiro
                  </Badge>
                )}
                {getShowLive(prize) && (
                  <Badge variant="default" className="gap-1 bg-green-600 hover:bg-green-600">
                    <Radio className="h-3 w-3" />
                    Ao Vivo
                  </Badge>
                )}
                {!prize.active && (
                  <Badge variant="destructive">Inativo</Badge>
                )}
              </div>
            </div>
            <CardContent className="p-4">
              <h3 className="font-semibold truncate">{prize.name}</h3>
              <p className="text-lg font-bold text-primary">{formatCurrency(prize.value)}</p>
              <div className="flex gap-2 mt-3">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => handleEdit(prize)}
                >
                  <Pencil className="h-4 w-4 mr-1" />
                  Editar
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setDeleteId(prize.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}

        {(!prizes || prizes.length === 0) && !isError && (
          <div className="col-span-full rounded-lg border border-dashed p-12 text-center">
            <Gift className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium">Nenhum prêmio cadastrado</h3>
            <p className="text-muted-foreground mb-4">
              Crie seu primeiro prêmio para usar nas raspadinhas
            </p>
            <Button onClick={handleCreate}>
              <Plus className="h-4 w-4 mr-2" />
              Criar Prêmio
            </Button>
          </div>
        )}
      </div>

      <PrizeFormDialog
        open={isFormOpen}
        onOpenChange={handleFormClose}
        prize={selectedPrize as any}
      />

      <ConfirmModal
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={() => { if (deleteId) deleteMutation.mutate(deleteId); }}
        title="Excluir prêmio?"
        description="Esta ação não pode ser desfeita. O prêmio será removido permanentemente."
        confirmText="Excluir"
        cancelText="Cancelar"
        variant="destructive"
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
}
